import React, { FC, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  IAluno,
  IFiltroDiarioClasse,
  schemaFiltroDiarioClasse,
} from "@deltasge/marauders-map";

import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

import { NumericFormat } from "react-number-format";

import { Environment, api, apiRelatorio } from "configs";
import { set as setConfigPapelPadrao } from "store/modules/relatorios/configuracoes/papel-padrao";
import { set as setModeloDiarioPadrao } from "store/modules/relatorios/diarios/modelo-padrao";
import { useAppDispatch, useAppSelector } from "store";
import * as styles from "assets/styles/form";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import { LoadingButton } from "@mui/lab";
import QueryString from "qs";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form } from "assets/styleds";

export interface IParamsDiario {
  turmaMateria: {
    idCurso: number;
    idSerie: number;
    idTurma: number;
    idMateria: number;
    idAluno?: number;
    idSistemaAvaliacao?: number | null;
  };
  close: () => void;
}

export const Diario: FC<IParamsDiario> = ({ turmaMateria, close }) => {
  const {
    tamanhoPapeis: { data: tamanhoPapeis },
    modelosDiarios: { data: modelosDiarios },
    configDiarioPadrao,
    configPapelPadrao,
    escola,
    configuracoes: { etapas },
    usuario: { usuario },
    layout: { devMode },
    sistemaAvaliacao: { data: sistemaAvaliacao },
  } = useAppSelector((state) => state);

  const { handleSubmit, control, watch, setValue } =
    useForm<IFiltroDiarioClasse>({
      defaultValues: {
        ...schemaFiltroDiarioClasse.getDefault(),
        ...turmaMateria,
        anoLetivo: escola.anoSite,
        ...configPapelPadrao,
        ...configDiarioPadrao,
        bimestre: escola.bimestre,
        modelo: configDiarioPadrao?.modelo || modelosDiarios[0]?.id,
      },
      resolver: yupResolver(schemaFiltroDiarioClasse),
    });

  const {
    bimestre,
    modelo,
    periodoRecuperacao,
    lado,
    html,
    usarFila,
    orientacao,
    papel,
    width,
    height,
    marginBottom,
    marginLeft,
    marginRight,
    marginTop,
  } = watch();

  const [modeloPadrao, setModeloPadrao] = useState(false);
  const [papelPadrao, setPapelPadrao] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [alunos, setAlunos] = useState<IAluno[]>([]);

  const dispatch = useAppDispatch();

  const etapasFiltradas = etapas
    .filter((f) => f.idsCursos?.includes(turmaMateria.idCurso))
    .sort((a, b) => a.etapa - b.etapa);

  const etapa = etapasFiltradas.find((f) => f.etapa == bimestre);

  const hasRecuperacao =
    !!etapa?.dataRecuperacaoIni && !!etapa?.dataRecuperacaoFim;

  const modeloDiario = modelosDiarios.find((f) => f.id == modelo);
  const modeloPapel = tamanhoPapeis.find((f) => f.name == papel);

  const onSubmit = async (data: IFiltroDiarioClasse) => {
    try {
      if (data.html && data.idMateria == -2) {
        throw new Error(
          "Esta opção não esta disponível para a emissão em HTML"
        );
      }
      setGerando(true);

      const params: Record<
        string,
        number | string | boolean | undefined | null
      > = {
        ...data,
        dataIni: format(data.dataIni, "yyyy-MM-dd"),
        dataFim: format(data.dataFim, "yyyy-MM-dd"),
        width: `${data.width}mm`,
        height: `${data.height}mm`,
        marginTop: `${data.marginTop}mm`,
        marginBottom: `${data.marginBottom}mm`,
        marginLeft: `${data.marginLeft}mm`,
        marginRight: `${data.marginRight}mm`,
      };
      if (modeloPadrao) {
        dispatch(
          setModeloDiarioPadrao({
            mostrarObservacoes: data.mostrarObservacoes,
            preencherDias: data.preencherDias,
            recuperacao: data.recuperacao,
            modelo: data.modelo,
            lado: data.lado,
          })
        );
      }
      if (papelPadrao) {
        dispatch(
          setConfigPapelPadrao({
            papel: data.papel,
            width: data.width,
            height: data.height,
            marginTop: data.marginTop,
            marginBottom: data.marginBottom,
            marginLeft: data.marginLeft,
            marginRight: data.marginRight,
            orientacao: data.orientacao,
          })
        );
      }
      const url = `/diario/${data.modelo}/${data.lado}/`;
      if (data.usarFila) {
        await apiRelatorio.get(url, {
          params,
          baseURL: `${Environment.RELATORIO_API_URL}/api/v2/${usuario?.escolaId}`,
          cache: { ignoreCache: true },
        });
        snack.info("Aguarde, o Diário de Classe será apresentado em breve");
      } else {
        params.authorization = `Bearer ${usuario?.token}`;
        const query = QueryString.stringify(params);
        window.open(
          `${Environment.RELATORIO_API_URL}/api/v2/${usuario?.escolaId}/diario/${data.modelo}/${data.lado}/?${query}`,
          "_blank"
        );
      }
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setGerando(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (turmaMateria.idTurma) {
        const where: Record<string, any> = {
          matriculas: {
            idTurma: turmaMateria.idTurma,
            notas: {
              recuperacao: {
                [`r${bimestre}`]: true,
              },
            },
          },
        };
        if (!periodoRecuperacao) {
          delete where.matriculas.notas;
        }
        const { data } = await api.get<[IAluno[], number]>(
          "aluno/matriculados",
          {
            cache: { ignoreCache: true },
            params: {
              where,
              select: ["nome", "uid", "nrChamada"],
              order: {
                nrChamada: "ASC",
              },
            },
          }
        );
        if (data && data[0] && Array.isArray(data[0])) setAlunos(data[0]);
      }
    })();
  }, [turmaMateria.idTurma, periodoRecuperacao, bimestre]);

  return (
    <>
      <Box sx={{ height: "100%", overflow: "auto", width: "100%" }}>
        <DialogContent dividers style={{ paddingBottom: 0 }}>
          <Form onSubmit={handleSubmit(onSubmit)} noValidate id="diario-form">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    ...styles.fieldset,
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1">Etapas</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="bimestre"
                        render={({ field, fieldState }) => {
                          const nomeEtapa =
                            sistemaAvaliacao?.nomeEtapa ||
                            escola.nomeEtapa ||
                            "Bimestre";
                          return (
                            <TextField
                              {...field}
                              select
                              label={nomeEtapa}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              InputLabelProps={{ shrink: true }}
                              SelectProps={{ displayEmpty: true }}
                              onChange={(e) => {
                                field.onChange(e);
                                const { value } = e.target;
                                if (!value) return;

                                const etapa = etapasFiltradas.find(
                                  (f) => f.etapa == parseInt(value, 10)
                                );
                                if (!etapa) return;

                                if (
                                  (!etapa.dataRecuperacaoIni ||
                                    !etapa.dataRecuperacaoFim) &&
                                  periodoRecuperacao
                                ) {
                                  setValue("periodoRecuperacao", false);
                                }

                                if (
                                  periodoRecuperacao &&
                                  etapa.dataRecuperacaoIni
                                )
                                  setValue(
                                    "dataIni",
                                    parseISO(etapa.dataRecuperacaoIni)
                                  );
                                else
                                  setValue(
                                    "dataIni",
                                    parseISO(etapa.dataEtapaIni)
                                  );

                                if (
                                  periodoRecuperacao &&
                                  etapa.dataRecuperacaoFim
                                )
                                  setValue(
                                    "dataFim",
                                    parseISO(etapa.dataRecuperacaoFim)
                                  );
                                else
                                  setValue(
                                    "dataFim",
                                    parseISO(etapa.dataEtapaFim)
                                  );
                              }}
                            >
                              {etapasFiltradas.map((etapa) => (
                                <MenuItem key={etapa.etapa} value={etapa.etapa}>
                                  {etapa.etapa}º {nomeEtapa}
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={!hasRecuperacao}
                            checked={periodoRecuperacao}
                            onChange={({ target: { checked } }) => {
                              setValue("periodoRecuperacao", checked);
                              const etapa = etapasFiltradas.find(
                                (f) => f.etapa == bimestre
                              );
                              if (!etapa) return;

                              if (checked && etapa.dataRecuperacaoIni)
                                setValue(
                                  "dataIni",
                                  parseISO(etapa.dataRecuperacaoIni)
                                );
                              else
                                setValue(
                                  "dataIni",
                                  parseISO(etapa.dataEtapaIni)
                                );

                              if (checked && etapa.dataRecuperacaoFim)
                                setValue(
                                  "dataFim",
                                  parseISO(etapa.dataRecuperacaoFim)
                                );
                              else
                                setValue(
                                  "dataFim",
                                  parseISO(etapa.dataEtapaFim)
                                );
                            }}
                          />
                        }
                        label="Gerar para o período de recuperação"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        control={control}
                        name="dataIni"
                        render={({ field, fieldState }) => (
                          <DatePicker
                            {...field}
                            label="Data Inicial"
                            value={field.value}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        control={control}
                        name="dataFim"
                        render={({ field, fieldState }) => (
                          <DatePicker
                            {...field}
                            label="Data Final"
                            value={field.value}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Box
                  sx={{
                    ...styles.fieldset,
                    p: 1,
                  }}
                >
                  <Typography variant="subtitle1">Modelos</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="modelo"
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            select
                            label="Diários de classe"
                            value={field.value}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{ displayEmpty: true }}
                            onChange={(e) => {
                              field.onChange(e);
                              const { value } = e.target;
                              if (!value) return;

                              const modelo = modelosDiarios.find(
                                (f) => f.id == parseInt(value, 10)
                              );
                              if (!modelo?.temVerso) {
                                setValue("lado", "frente");
                                setValue("mostrarObservacoes", false);
                              }
                              setValue(
                                "preencherDias",
                                modelo?.preenche == true
                              );
                            }}
                          >
                            {modelosDiarios.map((option, index) => (
                              <MenuItem key={index} value={option.id}>
                                {option.nome}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl disabled={!modeloDiario?.temVerso}>
                        <RadioGroup
                          row
                          name="frente-verso"
                          value={lado}
                          onChange={({ target: { value } }) => {
                            const lado = value as "frente" | "verso";
                            setValue("lado", lado);
                            if (lado == "frente")
                              setValue("mostrarObservacoes", false);
                          }}
                        >
                          <FormControlLabel
                            value="frente"
                            control={<Radio />}
                            label="Frente"
                          />
                          <FormControlLabel
                            value="verso"
                            control={<Radio />}
                            label="Verso"
                          />
                        </RadioGroup>
                      </FormControl>
                      <FormGroup>
                        <Controller
                          name="mostrarObservacoes"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  disabled={lado == "frente"}
                                  checked={field.value}
                                  {...field}
                                />
                              }
                              label="Imprimir observações no verso"
                            />
                          )}
                        />
                        <Controller
                          name="preencherDias"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.value}
                                  {...field}
                                  disabled={modeloDiario?.preenche != true}
                                />
                              }
                              label="Preencher o diário com os dias de aula"
                            />
                          )}
                        />
                        <Controller
                          name="recuperacao"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox checked={field.value} {...field} />
                              }
                              label="Somente alunos abaixo da média"
                            />
                          )}
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    ...styles.fieldset,
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1">Aluno</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="idAluno"
                        render={({ field, fieldState }) => {
                          return (
                            <TextField
                              {...field}
                              select
                              label="Aluno"
                              value={field.value || ""}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              InputLabelProps={{ shrink: true }}
                              SelectProps={{ displayEmpty: true }}
                              onChange={({ target: { value } }) =>
                                setValue(
                                  "idAluno",
                                  value ? parseInt(value, 10) : null
                                )
                              }
                            >
                              <MenuItem value="">Todos os alunos</MenuItem>
                              {alunos.map((aluno, index) => (
                                <MenuItem
                                  key={`aluno-${index}`}
                                  value={aluno.uid?.toString()}
                                >
                                  {aluno.nome}
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
                {devMode && (
                  <Box
                    sx={{
                      ...styles.fieldset,
                      p: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1">Debug</Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={usarFila}
                            onChange={({ target: { checked } }) => {
                              setValue("usarFila", checked);
                              setValue("html", checked ? false : html);
                            }}
                          />
                        }
                        label="Usar a fila para emissão do diário"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={usarFila}
                            checked={html}
                            onChange={({ target: { checked } }) =>
                              setValue("html", checked)
                            }
                            name="html"
                          />
                        }
                        label="Emitir diário em HTML"
                      />
                    </FormGroup>
                  </Box>
                )}
                <Box
                  sx={{
                    ...styles.fieldset,
                    p: 1,
                  }}
                >
                  <Typography variant="subtitle1">Opções</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="papel"
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Papel"
                            select
                            value={field.value}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{ displayEmpty: true }}
                            onChange={(e) => {
                              field.onChange(e);
                              const { value } = e.target;
                              if (!value) return;

                              const papel = tamanhoPapeis.find(
                                (f) => f.name == value
                              );

                              setValue("orientacao", "paisagem");

                              if (papel?.height)
                                setValue("height", papel?.height);
                              if (papel?.width) setValue("width", papel?.width);
                            }}
                          >
                            {tamanhoPapeis.map((option, index) => (
                              <MenuItem key={index} value={option.name}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl disabled={papel == "Customizado"}>
                        <RadioGroup
                          row
                          name="orientacao"
                          value={orientacao}
                          onChange={({ target: { value } }) => {
                            const orientacao = value as "retrato" | "paisagem";
                            setValue("orientacao", orientacao);
                            if (orientacao == "retrato" && modeloPapel) {
                              setValue("width", modeloPapel.height);
                              setValue("height", modeloPapel.width);
                            } else if (modeloPapel) {
                              setValue("width", modeloPapel.width);
                              setValue("height", modeloPapel.height);
                            }
                          }}
                        >
                          <FormControlLabel
                            value="paisagem"
                            control={<Radio />}
                            label="Paisagem"
                          />
                          <FormControlLabel
                            value="retrato"
                            control={<Radio />}
                            label="Retrato"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <NumericFormat
                        suffix=" mm"
                        label="Largura"
                        customInput={TextField}
                        value={width}
                        onValueChange={({ floatValue }) => {
                          if (floatValue) setValue("width", floatValue);
                        }}
                        allowNegative={false}
                        thousandSeparator={false}
                        disabled={papel != "Customizado"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <NumericFormat
                        suffix=" mm"
                        label="Altura"
                        customInput={TextField}
                        value={height}
                        onValueChange={({ floatValue }) => {
                          if (floatValue) setValue("height", floatValue);
                        }}
                        allowNegative={false}
                        thousandSeparator={false}
                        disabled={papel != "Customizado"}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          ...styles.fieldset,
                          p: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption">Margens</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <NumericFormat
                              suffix=" mm"
                              label="Topo"
                              customInput={TextField}
                              value={marginTop}
                              onValueChange={({ floatValue }) => {
                                if (floatValue)
                                  setValue("marginTop", floatValue);
                              }}
                              allowNegative={false}
                              thousandSeparator={false}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <NumericFormat
                              suffix=" mm"
                              label="Base"
                              customInput={TextField}
                              value={marginBottom}
                              onValueChange={({ floatValue }) => {
                                if (floatValue)
                                  setValue("marginBottom", floatValue);
                              }}
                              allowNegative={false}
                              thousandSeparator={false}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <NumericFormat
                              suffix=" mm"
                              label="Esquerda"
                              customInput={TextField}
                              value={marginLeft}
                              onValueChange={({ floatValue }) => {
                                if (floatValue)
                                  setValue("marginLeft", floatValue);
                              }}
                              allowNegative={false}
                              thousandSeparator={false}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <NumericFormat
                              suffix=" mm"
                              label="Direita"
                              customInput={TextField}
                              value={marginRight}
                              onValueChange={({ floatValue }) => {
                                if (floatValue)
                                  setValue("marginRight", floatValue);
                              }}
                              allowNegative={false}
                              thousandSeparator={false}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Form>
        </DialogContent>
      </Box>
      <DialogActions>
        <FormGroup row>
          <FormControlLabel
            control={
              <Checkbox
                value={modeloPadrao}
                onChange={({ target: { checked } }) => setModeloPadrao(checked)}
              />
            }
            label="Modelo padrão"
          />
          <FormControlLabel
            control={
              <Checkbox
                value={papelPadrao}
                onChange={({ target: { checked } }) => setPapelPadrao(checked)}
              />
            }
            label="Configuração de papel padrão"
          />
        </FormGroup>
        <Button onClick={close} color={"secondary"}>
          Cancelar
        </Button>
        <LoadingButton
          type="submit"
          variant="outlined"
          loading={gerando}
          form="diario-form"
        >
          Visualizar
        </LoadingButton>
      </DialogActions>
    </>
  );
};
